import * as React from "react";
import { MoneyDisplayHelper } from "../../../../../../../../react-core/Helpers/MoneyDisplayHelper";
import { StringHelper } from "../../../../../../../../react-core/Helpers/StringHelper";
import { PerformanceMethod } from "../../../../../../../../Wrap/Constants/Enums/Domain/PerformanceMethod";
import { UserClaim } from "../../../../../../../../Wrap/Constants/Enums/UserClaim";
import { CashTransactionTypes } from "../../../../../../../../Wrap/Models/Domains/Transactions/Cash/CashTransactionTypes";
import { PortfolioPerformanceSummaryModel } from "../../../../../../../../Wrap/Models/Models/PortfolioAnalysis/Performance/PortfolioPerformanceSummaryModel";
import { Alert } from "../../../../../../../Shared/Components/Alert";
import { AlertType } from "../../../../../../../Shared/Entities/Constants/AlertConstants";
import AdviserSiteTagManager, {
    eventCategoryForGTM,
} from "../../../../../../../Shared/Cookies/ChoicesCookie/AdviserSiteTagManager";
import { Permission } from "../../../../../../../Shared/Permission/Permission";
import { Table } from "../../../../../../../Shared/Table/Table";
import { AriaTooltip } from "../../../../../../../Shared/Tooltip/AriaTooltip";
import { Contents } from "../../Insights/Resource/Contents";
import { tabContent } from "../../../Resource/Contents";
import { returnTooltip } from "../Resource/Contents";
import { TransactionEnum } from "../../Transactions/Resource/Contents";
import { Spinner } from "../../../../../../../../react-core/Html/Spinner";

interface ISummaryTableProps {
    data: PortfolioPerformanceSummaryModel;
    return: PerformanceMethod;
    isTableLoading: boolean;
    navigationToTab: (tab: string, transTypeSelect: TransactionEnum, cashTransactionTypes?) => void;
    from: string;
    to: string;
    claccountid: string;
    subaccountClaccountids: string[];
    selectedHeadClAccountIds: string[];
}

export const SummaryTable = (props: ISummaryTableProps) => {
    if (props.isTableLoading) {
        return (
            <div className="text-center">
                <h3>Performance details loading</h3>
                <p>
                    This may take a few moments. Please do not move away from this page.
                </p>
                <Spinner notFull={true} />
            </div>
        );
    }
    if (!props.data) {return null;}
    if (props.data.ProductPerformanceSummary.length == 0 && !props.isTableLoading)
        {return <Alert alertType={AlertType.Warning} message={Contents.noDataErrorMessage} />;}
    const stringHelper = new StringHelper();
    const { addEvent } = new AdviserSiteTagManager();
    const subaccountstring = props.subaccountClaccountids.join(',');
    return (
        <Table
            data={props.data.ProductPerformanceSummary}
            rowCssWrapper={"product-heading"}
            isLoading={props.isTableLoading}
            col={[
                {
                    title: "Name",
                    key: "ClAccountId",
                    render: (data, row, index) => {                      
                        if(typeof row.ProductType == "string" && parseInt(row.ProductType) > 0)
                        {
                            row.ProductType = parseInt(row.ProductType);
                        }
                        return (
                            <>
                                <span
                                    aria-hidden="true"
                                    className={"product-legend" + stringHelper.getDonutClass(row.ProductType, row.WrapperSubType)}
                                ></span>
                                <span>{row.ProductDisplayName}</span>
                                <span className="account">{data}</span>
                            </>
                        );
                    },
                },
                {
                    title: "Opening value",
                    key: "OpeningValue",
                    thCssWrapper: "cell-right",
                    tdCssWrapper: "cell-right",
                    render: (data, row, index) => {
                        return MoneyDisplayHelper.getMoneyFormat(data);
                    },
                },
                {
                    title: (
                        <Permission
                            userClaim={UserClaim.AdviserPortalInvestmentTransactionCanView}
                            replaceChildren={<>Money in</>}
                        >
                            <button
                                className="hyperlink mb-5 text-right text-bold"
                                onClick={() => {
                                    addEvent({
                                        event: "performancetab_interaction",
                                        eventParameters: {
                                            eventCategory: eventCategoryForGTM.performanceTab,
                                            eventAction: "interactions",
                                            eventLabel: "money in",
                                        },
                                    });
                                    props.navigationToTab(
                                        tabContent.Transactions,
                                        TransactionEnum.cash,
                                        CashTransactionTypes.CapitalIn
                                    );
                                }}
                            >
                                Money in
                            </button>
                        </Permission>
                    ),
                    key: "MoneyIn",
                    thCssWrapper: "cell-right",
                    tdCssWrapper: "cell-right",
                    render: (data, row, index) => {
                        return MoneyDisplayHelper.getMoneyFormat(data);
                    },
                },
                {
                    title: (
                        <Permission
                            userClaim={UserClaim.AdviserPortalInvestmentTransactionCanView}
                            replaceChildren={<>Money out</>}
                        >
                            <button
                                className="hyperlink mb-5 text-right text-bold"
                                onClick={() => {
                                    addEvent({
                                        event: "performancetab_interaction",
                                        eventParameters: {
                                            eventCategory: eventCategoryForGTM.performanceTab,
                                            eventAction: "interactions",
                                            eventLabel: "money out",
                                        },
                                    });
                                    props.navigationToTab(
                                        tabContent.Transactions,
                                        TransactionEnum.cash,
                                        CashTransactionTypes.CapitalOut
                                    );
                                }}
                            >
                                Money out
                            </button>
                        </Permission>
                    ),
                    key: "MoneyOut",
                    thCssWrapper: "cell-right",
                    tdCssWrapper: "cell-right",
                    render: (data, row, index) => {
                        return MoneyDisplayHelper.getMoneyFormat(data);
                    },
                },
                {
                    title: (
                        <Permission
                            userClaim={UserClaim.AdviserPortalAccountTransfersCanView}
                            replaceChildren={<>Account transfers</>}
                        >
                            <button
                                className="hyperlink mb-5 text-right text-bold"
                                onClick={() => {
                                    addEvent({
                                        event: "performancetab_interaction",
                                        eventParameters: {
                                            eventCategory: eventCategoryForGTM.performanceTab,
                                            eventAction: "interactions",
                                            eventLabel: "account transfers",
                                        },
                                    });
                                    props.navigationToTab(tabContent.Transactions, TransactionEnum.accountTransfers);
                                }}
                            >
                                Account transfers
                            </button>
                        </Permission>
                    ),
                    key: "AccountTransfer",
                    thCssWrapper: "cell-right",
                    tdCssWrapper: "cell-right",
                    render: (data, row, index) => {
                        return MoneyDisplayHelper.getMoneyFormat(data);
                    },
                },
                {
                    title: "Change in value",
                    key: "TotalGainLoss",
                    thCssWrapper: "cell-right",
                    tdCssWrapper: "cell-right",
                    render: (data, row, index) => {
                        return (
                            <span className={data.Amount >= 0 ? "text-positive" : "text-negative"}>
                                {MoneyDisplayHelper.getMoneyFormat(data, true, 2)}
                            </span>
                        );
                    },
                },
                {
                    title:
                        props.return == PerformanceMethod.IRR ? (
                            <Permission
                                userClaim={UserClaim.AdviserPortalClientSummaryCanView}
                                replaceChildren={<span>Return %</span>}
                            >
                                <a
                                    className="hyperlink mb-5 text-right text-bold"
                                    target="_blank"
                                    href={`/clients/performance/performance-return.aspx?claccountid=${props.claccountid}&subclaccountids=${subaccountstring}&selectedheadclaccounds=${props.selectedHeadClAccountIds}&fromdate=${props.from}&todate=${props.to}&type=IRRGross`}
                                    onClick={() => {
                                        addEvent({
                                            event: "performancetab_interaction",
                                            eventParameters: {
                                                eventAction: "interactions",
                                                eventCategory: eventCategoryForGTM.performanceTab,
                                                eventLabel: `Return %`,
                                            },
                                        });
                                    }}
                                >
                                    <span>
                                        Return %
                                        <AriaTooltip
                                            cssWrapper="is-info-icon ml-6"
                                            showCloseButton={true}
                                            title="More info on simple return"
                                        >
                                            <b>{returnTooltip.title[props.return]}</b> -{" "}
                                            {returnTooltip.content[props.return]}
                                        </AriaTooltip>
                                    </span>
                                </a>
                            </Permission>
                        ) : (
                            <span>
                                Return %
                                <AriaTooltip
                                    cssWrapper="is-info-icon ml-6"
                                    showCloseButton={true}
                                    title="More info on simple return"
                                >
                                    <b>{returnTooltip.title[props.return]}</b> - {returnTooltip.content[props.return]}
                                </AriaTooltip>
                            </span>
                        ),
                    key: "TotalGainLossPercentage",
                    thCssWrapper: "cell-right",
                    tdCssWrapper: "cell-right",
                    render: (data, row, index) => {
                        if (row.ProductType == "CashAccount" || row.ProductType == "WrapCash") {return <span>N/A</span>;}
                        return (
                            <span className={row.TotalGainLoss.Amount >= 0 ? "text-positive" : "text-negative"}>
                                {Number(data).toFixed(2) + "%"}
                            </span>
                        );
                    },
                },
                {
                    title: "Closing value",
                    key: "ClosingValue",
                    thCssWrapper: "cell-right",
                    tdCssWrapper: "cell-right",
                    render: (data, row, index) => {
                        return MoneyDisplayHelper.getMoneyFormat(data);
                    },
                },
            ]}
            TotalBody={[
                { data: "Total" },
                {
                    data: MoneyDisplayHelper.getMoneyFormat(props.data.OpeningValue),
                    tfTdCssWrapper: "cell-right",
                },
                {
                    data: MoneyDisplayHelper.getMoneyFormat(props.data.MoneyIn),
                    tfTdCssWrapper: "cell-right",
                },
                {
                    data: MoneyDisplayHelper.getMoneyFormat(props.data.MoneyOut),
                    tfTdCssWrapper: "cell-right",
                },
                {
                    data: MoneyDisplayHelper.getMoneyFormat(props.data.AccountTransfer),
                    tfTdCssWrapper: "cell-right",
                },
                {
                    data: (
                        <span className={props.data.TotalGainLoss.Amount >= 0 ? "text-positive" : "text-negative"}>
                            {MoneyDisplayHelper.getMoneyFormat(props.data.TotalGainLoss, true, 2)}
                        </span>
                    ),
                    tfTdCssWrapper: "cell-right",
                },
                {
                    data: (
                        <span className={props.data.TotalGainLossPercentage >= 0 ? "text-positive" : "text-negative"}>
                            {Number(props.data.TotalGainLossPercentage).toFixed(2) + "%"}
                        </span>
                    ),
                    tfTdCssWrapper: "cell-right",
                },
                {
                    data: MoneyDisplayHelper.getMoneyFormat(props.data.ClosingValue),
                    tfTdCssWrapper: "cell-right",
                },
            ]}
        />
    );
};
